import { Controller } from "@hotwired/stimulus"

// Asistencia visual para la validación de facturas:
// - Recalcula subtotal + IVA $ por línea de item en vivo
// - Recalcula total general cuando cambia neto/IVA/otros/descuentos
// - Muestra "12 de marzo de 2026" cuando el operador cambia la fecha
//   (evita confusión MM/DD/YYYY vs DD/MM/YYYY)
export default class extends Controller {
  static targets = [
    "lineQty", "linePrice", "lineDescuento", "lineDescPct", "lineIvaRate", "lineSubtotal",
    "netAmount", "totalIva", "otrosTributos", "descuentos", "totalDisplay",
    "dateInput", "dateLabel", "supplierId"
  ]

  connect() {
    this.recalcAllLines()
    this.recalcTotal()
  }

  // ── Línea de item ────────────────────────────────────────────
  recalcLine(event) {
    const row = event.target.closest("tr")
    if (!row) return
    this.recalcRow(row)
  }

  recalcRow(row) {
    const qty = parseFloat(row.querySelector('[data-invoice-validation-target="lineQty"]')?.value) || 0
    const pricePesos = parseFloat(row.querySelector('[data-invoice-validation-target="linePrice"]')?.value) || 0
    const descPesos = parseFloat(row.querySelector('[data-invoice-validation-target="lineDescuento"]')?.value) || 0
    const ivaRate = parseFloat(row.querySelector('[data-invoice-validation-target="lineIvaRate"]')?.value) || 0

    const brutoCents = Math.round(qty * pricePesos * 100)
    const netoCents = brutoCents - Math.round(descPesos * 100)

    const subtotalCell = row.querySelector('[data-invoice-validation-target="lineSubtotal"]')
    if (subtotalCell) subtotalCell.innerHTML = "<strong>" + this.fmt(netoCents) + "</strong>"

    // Almacenar el subtotal calculado para sumar después
    if (subtotalCell) subtotalCell.dataset.cents = netoCents

    // Trigger cascada: actualizar Neto gravado y Total general
    this.recalcNetFromItems()
  }

  recalcAllLines() {
    this.element.querySelectorAll("tbody tr").forEach(row => this.recalcRow(row))
    this.recalcNetFromItems()
  }

  // Editaste Desc % → calcula Desc $ = qty × precio × % / 100, dispara recalcRow
  descPctChanged(event) {
    const row = event.target.closest("tr")
    if (!row) return
    const qty = parseFloat(row.querySelector('[data-invoice-validation-target="lineQty"]')?.value) || 0
    const price = parseFloat(row.querySelector('[data-invoice-validation-target="linePrice"]')?.value) || 0
    const pct = parseFloat(event.target.value) || 0
    const descPesos = (qty * price * pct / 100).toFixed(2)
    const descInput = row.querySelector('[data-invoice-validation-target="lineDescuento"]')
    if (descInput) descInput.value = descPesos
    this.recalcRow(row)
  }

  // Editaste Desc $ → calcula Desc % = desc / (qty × precio) × 100, dispara recalcRow
  descAmountChanged(event) {
    const row = event.target.closest("tr")
    if (!row) return
    const qty = parseFloat(row.querySelector('[data-invoice-validation-target="lineQty"]')?.value) || 0
    const price = parseFloat(row.querySelector('[data-invoice-validation-target="linePrice"]')?.value) || 0
    const desc = parseFloat(event.target.value) || 0
    const bruto = qty * price
    const pct = bruto > 0 ? (desc / bruto * 100).toFixed(2) : "0.00"
    const pctInput = row.querySelector('[data-invoice-validation-target="lineDescPct"]')
    if (pctInput) pctInput.value = pct
    this.recalcRow(row)
  }

  // Suma los subtotales de todos los items y actualiza el campo Neto gravado.
  // Eso dispara recalcTotal automáticamente porque está bindeado al input.
  recalcNetFromItems() {
    if (!this.hasNetAmountTarget) return
    let sumCents = 0
    this.lineSubtotalTargets.forEach(cell => {
      sumCents += parseInt(cell.dataset.cents || 0, 10)
    })
    this.netAmountTarget.value = (sumCents / 100).toFixed(2)
    this.recalcTotal()
  }

  // ── Total general ────────────────────────────────────────────
  recalcTotal() {
    const neto    = this.parsePesos(this.netAmountTarget?.value)
    const iva     = this.parsePesos(this.totalIvaTarget?.value)
    const otros   = this.parsePesos(this.otrosTributosTarget?.value)
    const desc    = this.parsePesos(this.descuentosTarget?.value)
    const totalCents = Math.round((neto + iva + otros - desc) * 100)

    if (this.hasTotalDisplayTarget) {
      this.totalDisplayTarget.innerHTML = this.fmt(totalCents)
    }
  }

  // ── Supplier: matchear con la opción del datalist y guardar el ID ───
  supplierChanged(event) {
    const value = event.target.value
    const datalist = document.getElementById("suppliers-datalist")
    if (!datalist) return
    const option = Array.from(datalist.options).find(o => o.value === value)
    if (option && this.hasSupplierIdTarget) {
      this.supplierIdTarget.value = option.dataset.id || ""
      // Si el supplier tiene CUIT, autocompletar el campo CUIT
      const cuit = option.dataset.cuit
      if (cuit) {
        const cuitInput = document.querySelector('input[name="header[billing_cuit]"]')
        if (cuitInput && !cuitInput.value) cuitInput.value = cuit
      }
    }
  }

  // ── Fecha: mostrar versión legible ───────────────────────────
  updateDateLabel(event) {
    if (!this.hasDateLabelTarget) return
    const v = event.target.value
    if (!v) {
      this.dateLabelTarget.innerHTML = ""
      return
    }
    const [y, m, d] = v.split("-").map(n => parseInt(n, 10))
    const meses = ["enero", "febrero", "marzo", "abril", "mayo", "junio",
                   "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"]
    const human = `${d} de ${meses[m - 1]} de ${y}`
    this.dateLabelTarget.innerHTML = `⚠️ Verificá: <strong>${human}</strong> (DD/MM/AAAA — formato argentino)`
  }

  // ── Helpers ──────────────────────────────────────────────────
  parsePesos(v) { return parseFloat(v) || 0 }
  fmt(cents) {
    const v = (cents / 100).toFixed(2)
    return "$" + v.replace(/\B(?=(\d{3})+(?!\d))/g, ".")
  }
}
